import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { upsertPageBySlug, fetchPageBySlug, clearPageState } from "../redux/slices/pageSlice";

// CKEditor dynamic import (for React, no Next.js SSR)
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const PageEditor = ({ initialSlug = "" }) => {
  const dispatch = useDispatch();
  const { pageData, loading, error, success } = useSelector((state) => state.page);

  const [slug, setSlug] = useState(initialSlug);
  const [pageTitle, setPageTitle] = useState("");
  const [pageDesc, setPageDesc] = useState("");
  const [content, setContent] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");
  const [relatedButtons, setRelatedButtons] = useState([]);
  const [faqs, setFaqs] = useState([]); // ✅ NEW: FAQ state

  // Load page data if editing
  useEffect(() => {
    if (slug) {
      dispatch(fetchPageBySlug(slug));
    }
    return () => dispatch(clearPageState());
  }, [dispatch, slug]);

  useEffect(() => {
    if (pageData) {
      setPageTitle(pageData.pageTitle || "");
      setPageDesc(pageData.pageDesc || "");
      setContent(pageData.content || "");
      setMetaTitle(pageData.metaTitle || "");
      setMetaDescription(pageData.metaDescription || "");
      setMetaKeywords((pageData.metaKeywords || []).join(", "));
      setRelatedButtons(pageData.relatedButtons || []);
      setFaqs(pageData.faqs || []); // ✅ NEW: Load FAQs
    }
  }, [pageData]);

  // ✅ Related Buttons handlers (existing)
  const addButton = () => setRelatedButtons([...relatedButtons, { buttonSlug: "", buttonTitle: "" }]);
  const removeButton = (index) => {
    const updated = [...relatedButtons];
    updated.splice(index, 1);
    setRelatedButtons(updated);
  };
  const updateButton = (index, field, value) => {
    const updated = [...relatedButtons];
    updated[index][field] = value;
    setRelatedButtons(updated);
  };

  // ✅ NEW: FAQ handlers
  const addFaq = () => setFaqs([...faqs, { question: "", answer: "" }]);
  const removeFaq = (index) => {
    const updated = [...faqs];
    updated.splice(index, 1);
    setFaqs(updated);
  };
  const updateFaq = (index, field, value) => {
    const updated = [...faqs];
    updated[index][field] = value;
    setFaqs(updated);
  };

  const handleSave = () => {
    if (!slug) return alert("⚠️ Slug is required!");

    dispatch(
      upsertPageBySlug({
        slug,
        pageData: {
          pageTitle,
          pageDesc,
          content,
          metaTitle,
          metaDescription,
          metaKeywords: metaKeywords.split(",").map((kw) => kw.trim()),
          relatedButtons,
          faqs, // ✅ NEW: Include FAQs in save
        },
      })
    );
  };

  return (
    <div className="container mt-4">
      <div className="card">
        <div className="card-header">
          <h4 className="mb-3">Page Editor</h4>
        </div>

        <div className="card-body">
          {/* Slug */}
          <div className="mb-3">
            <label className="form-label">Slug</label>
            <input
              type="text"
              className="form-control"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="e.g. json-formatter"
            />
            <small className="text-muted">Slug will be used in the page URL</small>
          </div>

          {/* Page Title */}
          <div className="mb-3">
            <label className="form-label">Title</label>
            <input
              type="text"
              className="form-control"
              value={pageTitle}
              onChange={(e) => setPageTitle(e.target.value)}
              placeholder="Enter page title"
            />
          </div>

          {/* Page Description */}
          <div className="mb-3">
            <label className="form-label">Page Description</label>
            <textarea
              className="form-control"
              rows="3"
              value={pageDesc}
              onChange={(e) => setPageDesc(e.target.value)}
              placeholder="Short description about this page"
            />
          </div>

          {/* Meta Fields */}
          <div className="mb-3">
            <label className="form-label">Meta Title</label>
            <input
              type="text"
              className="form-control"
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              placeholder="Meta Title for SEO"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Meta Description</label>
            <textarea
              className="form-control"
              rows="3"
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              placeholder="Meta Description for SEO"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Meta Keywords (comma separated)</label>
            <input
              type="text"
              className="form-control"
              value={metaKeywords}
              onChange={(e) => setMetaKeywords(e.target.value)}
              placeholder="json, formatter, tool"
            />
          </div>

          {/* CKEditor */}
          <div className="mb-3">
            <label className="form-label">Content</label>
            <CKEditor
              editor={ClassicEditor}
              data={content}
              onChange={(_, editor) => setContent(editor.getData())}
            />
          </div>

          {/* ✅ NEW: FAQs Section */}
          <div className="mb-3">
            <label className="form-label">FAQs</label>
            <div style={{ backgroundColor: "#f8f9fa", padding: "15px", borderRadius: "5px", marginBottom: "10px" }}>
              {faqs.length === 0 ? (
                <p className="text-muted mb-0">No FAQs added yet. Add your first FAQ below.</p>
              ) : (
                faqs.map((faq, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: "#fff",
                      padding: "12px",
                      marginBottom: "10px",
                      borderRadius: "4px",
                      border: "1px solid #e9ecef"
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <small className="text-muted">FAQ #{index + 1}</small>
                    </div>
                    <textarea
                      className="form-control mb-2"
                      placeholder="Question"
                      value={faq.question}
                      onChange={(e) => updateFaq(index, "question", e.target.value)}
                      rows="2"
                      style={{ fontSize: "14px" }}
                    />
                    <textarea
                      className="form-control mb-2"
                      placeholder="Answer (HTML supported)"
                      value={faq.answer}
                      onChange={(e) => updateFaq(index, "answer", e.target.value)}
                      rows="3"
                      style={{ fontSize: "14px" }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => removeFaq(index)}
                    >
                      ✕ Remove FAQ
                    </button>
                  </div>
                ))
              )}
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={addFaq}
            >
              + Add FAQ
            </button>
          </div>

          {/* Related Buttons */}
          <div className="mb-3">
            <label className="form-label">Related Buttons</label>
            {relatedButtons.map((btn, index) => (
              <div className="d-flex mb-2" key={index}>
                <input
                  type="text"
                  className="form-control me-2"
                  placeholder="Button Slug"
                  value={btn.buttonSlug}
                  onChange={(e) => updateButton(index, "buttonSlug", e.target.value)}
                />
                <input
                  type="text"
                  className="form-control me-2"
                  placeholder="Button Title"
                  value={btn.buttonTitle}
                  onChange={(e) => updateButton(index, "buttonTitle", e.target.value)}
                />
                <button type="button" className="btn btn-danger" onClick={() => removeButton(index)}>
                  Remove
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-secondary" onClick={addButton}>
              + Add Related Button
            </button>
          </div>

          {/* Save Button */}
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? "Saving..." : "Save Page"}
          </button>

          {success && <p className="text-success mt-2">✅ Page saved successfully!</p>}
          {error && <p className="text-danger mt-2">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default PageEditor;
